import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { availabilityImportEndpoints } from '@ooh/contracts';
import type { AvailabilityImportRematchRow, AvailabilityProfile } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';

/** Rematch-relevant profile fields — the operator can correct any of these
 * after resolution and change which unit a row matches. Any other profile
 * edit (statusColumn, date formats, period labels, etc.) recomputes
 * client-side (`deriveInterpretedRows`) and must NOT trigger a rematch call.
 *
 * NOTE: this fingerprint only covers the fields the UI currently lets the
 * operator edit. The server's actual matching (`resolveRowToUnit` via
 * `extractRowIdentifiers`) also considers `gpsColumns`/`addressColumns` —
 * not editable here today, so omitting them is safe for now. If a future
 * feature adds GPS/address editing to the preview UI, this fingerprint MUST
 * be extended too, or edits to those fields will silently fail to trigger a
 * rematch. */
function matchRelevantKey(profile: AvailabilityProfile): string {
  return JSON.stringify({
    supplierNumberColumn: profile.idColumns.supplierNumberColumn,
    grwIdColumn: profile.idColumns.grwIdColumn,
    supplierColumn: profile.supplierColumn,
    defaultSupplierName: profile.defaultSupplierName,
  });
}

/**
 * Debounces the matching-relevant subset of `profile` (see
 * `matchRelevantKey`) and returns a snapshot only once `delayMs` has passed
 * with no further changes to those fields — `null` while waiting, or while
 * the fields still match their initial (already-fetched-from-preview)
 * values, since a rematch call would be redundant there.
 */
function useDebouncedMatchProfile(
  profile: AvailabilityProfile,
  delayMs: number,
): AvailabilityProfile | null {
  const currentKey = matchRelevantKey(profile);
  const initialKeyRef = React.useRef(currentKey);
  const [settled, setSettled] = React.useState<{
    key: string;
    profile: AvailabilityProfile;
  } | null>(null);

  React.useEffect(() => {
    if (currentKey === initialKeyRef.current) return;
    const id = setTimeout(() => setSettled({ key: currentKey, profile }), delayMs);
    return () => clearTimeout(id);
  }, [currentKey, profile, delayMs]);

  return settled?.key === currentKey ? settled.profile : null;
}

/** Debounce window (ms) between the operator's last matching-relevant edit
 * and the rematch call — long enough to avoid firing on every intermediate
 * select change, short enough to feel responsive. */
const REMATCH_DEBOUNCE_MS = 500;

export interface UseRematchedRowsResult {
  rows: AvailabilityImportRematchRow[] | undefined;
  /** True when the debounced rematch request itself failed (network blip,
   * 500, auth expiry) — the caller should surface this rather than silently
   * keeping the stale pre-edit match state (see `preview-phase.tsx`'s
   * inline warning). */
  isError: boolean;
}

/**
 * Debounced-calls `POST /availability-import/:id/rematch` whenever the
 * operator edits a field that affects row-to-unit matching, returning the
 * recomputed rows so the caller can merge them into the displayed preview
 * rows by `rowNumber` (see `preview-phase.tsx`'s `mergeRematchedRows`).
 *
 * The call is a mutation, not a query: it is a conditional, read-only
 * recompute that must never suspend the preview. Rows are returned only
 * while they belong to the current matching fields, so a pending edit falls
 * back to the initial preview rows instead of showing a stale match.
 */
export function useRematchedRows(
  jobId: number,
  profile: AvailabilityProfile,
): UseRematchedRowsResult {
  const api = useApiClient();
  const debouncedProfile = useDebouncedMatchProfile(profile, REMATCH_DEBOUNCE_MS);
  const { mutate, data, isError, variables } = useMutation({
    mutationFn: ({ profile: matchProfile }: { key: string; profile: AvailabilityProfile }) =>
      api.call(availabilityImportEndpoints.rematch, { id: jobId, profile: matchProfile }),
  });

  React.useEffect(() => {
    if (debouncedProfile === null) return;
    mutate({ key: matchRelevantKey(debouncedProfile), profile: debouncedProfile });
  }, [debouncedProfile, mutate]);

  const isCurrent = variables?.key === matchRelevantKey(profile);
  return { rows: isCurrent ? data?.rows : undefined, isError: isCurrent && isError };
}
